// src/tabs/logements/RetranslateConfirmModal.jsx — M7 (éditeur livret admin)
//
// Modale de confirmation AVANT la re-traduction avec écrasement (« Re-traduire
// tout ») — GO produit Dimitri : « confirmation explicite avant », jamais de
// window.confirm (pattern overlay des modales du dossier, cf.
// CreateCategoryModal).
//
// Condition pessimist B3 : `count` = nombre de champs EN/ES NON VIDES qui
// seront écrasés (calculé LOCALEMENT par le parent via countOverwritableLangs,
// 0 appel réseau) — affiché DANS la modale.
//
// NOTE HONNÊTE (pessimist B2) : l'idempotence de la re-traduction n'est PAS
// garantie — le cache serveur de livret_translate est par-lambda (cold start
// = cache froid), un re-run peut re-patcher des traductions quasi identiques.
// Best-effort assumé, jamais promis (ni ici, ni dans le message de fin de
// passe qui le rappelle).
//
// a11y (miroir CreateCategoryModal) :
//   - role=dialog + aria-modal=true + aria-labelledby
//   - Escape ferme la modale
//   - Clic overlay (hors carte) ferme la modale
//   - Boutons minHeight 44

import { useEffect } from "react";
import { DS } from "../../lib/ui";
import { AlertTriangle } from "lucide-react";

export default function RetranslateConfirmModal({ count, onClose, onConfirm }) {
  // Escape handler
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="retranslate-confirm-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(33,83,135,0.45)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: DS.fontBody,
      }}
    >
      <div
        className="fbz-modal-sheet"
        style={{
          background: DS.white,
          borderRadius: 14,
          padding: 22,
          maxWidth: 480,
          width: "100%",
          maxHeight: "90vh",
          overflowY: "auto",
          boxShadow: "0 12px 40px rgba(0,0,0,0.2)",
        }}
      >
        <h3
          id="retranslate-confirm-title"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            margin: "0 0 14px",
            fontFamily: DS.fontDisplay,
            color: DS.marineDark,
            fontSize: 18,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              display: "inline-flex",
              width: 32,
              height: 32,
              borderRadius: 9,
              background: DS.dangerBg,
              color: DS.danger,
              alignItems: "center",
              justifyContent: "center",
              flex: "0 0 auto",
            }}
          >
            <AlertTriangle size={17} strokeWidth={1.8} />
          </span>
          Re-traduire tout le livret ?
        </h3>

        <p
          style={{
            margin: "0 0 16px",
            fontSize: 13,
            lineHeight: 1.6,
            color: DS.textSecondary,
          }}
        >
          <strong style={{ color: DS.dangerText }}>
            {count} version(s) EN/ES existante(s)
          </strong>{" "}
          — y compris vos corrections manuelles — seront remplacées par une
          nouvelle traduction automatique du français. Le français n'est jamais
          modifié. Action non annulable.
        </p>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button
            type="button"
            onClick={onClose}
            className="fbz-press"
            style={{
              padding: "8px 14px",
              background: DS.white,
              color: DS.textSecondary,
              border: `1px solid ${DS.borderHover}`,
              borderRadius: 7,
              cursor: "pointer",
              fontFamily: DS.fontBody,
              fontSize: 12,
              fontWeight: 600,
              minHeight: 44,
            }}
          >
            Annuler
          </button>
          {/* Bouton DESTRUCTIF (DS.danger) — l'écrasement est irréversible. */}
          <button
            type="button"
            onClick={onConfirm}
            className="fbz-press"
            style={{
              padding: "8px 14px",
              background: DS.danger,
              color: DS.white,
              border: "none",
              borderRadius: 7,
              cursor: "pointer",
              fontFamily: DS.fontBody,
              fontSize: 12,
              fontWeight: 700,
              minHeight: 44,
            }}
          >
            Écraser et re-traduire
          </button>
        </div>
      </div>
    </div>
  );
}
